import { useCallback, useEffect, useState } from "react";
import {
  PROMPTS,
  activateInstallation,
  aggregateFromDays,
  localDateKey,
  promptForDay,
  syncProgress,
  type AgeGroup,
  type DayStatus,
  type DigStorage,
  type LocalDayRecord,
} from "../index";
import "./client.css";

export interface DigNotifications {
  requestPermission(): Promise<boolean>;
  scheduleDaily(time: string): Promise<void>;
  scheduleSnooze(): Promise<void>;
  cancelSnooze(): Promise<void>;
}

export interface DigClientAppProps {
  storage: DigStorage;
  shareToken: string | null;
  ageGroupFromLink: AgeGroup | null;
  notifications?: DigNotifications;
  /** Dev-only browser client; production uses native app */
  allowActivation?: boolean;
  /** 5 taps on landing unlocks offline demo (native app) */
  enableDemoUnlock?: boolean;
}

type Phase = "loading" | "landing" | "onboarding" | "time" | "activate" | "main" | "score" | "expired";

const AGE_GROUPS: AgeGroup[] = ["8-11", "12-15", "16-18"];

const AGE_LABELS: Record<AgeGroup, string> = {
  "8-11": "8–11 jaar",
  "12-15": "12–15 jaar",
  "16-18": "16–18 jaar",
};

function defaultTime(): string {
  return "09:00";
}

export function DigClientApp({
  storage,
  shareToken,
  ageGroupFromLink,
  notifications,
  allowActivation = false,
  enableDemoUnlock = false,
}: DigClientAppProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(ageGroupFromLink);
  const [preferredTime, setPreferredTime] = useState(defaultTime());
  const [today, setToday] = useState<LocalDayRecord | null>(null);
  const [promptText, setPromptText] = useState("");
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [landingTaps, setLandingTaps] = useState(0);

  const refreshToday = useCallback(
    async (seed: string, group: AgeGroup) => {
      const date = localDateKey();
      const days = await storage.getDays();
      let record = days.find((d) => d.localDate === date) ?? null;
      const prompt = promptForDay(PROMPTS, group, seed);
      if (!record) {
        record = { localDate: date, promptId: prompt.id, status: "pending" };
        await storage.upsertDay(record);
      }
      setToday(record);
      setPromptText(PROMPTS.find((p) => p.id === record!.promptId)?.text ?? prompt.text);
    },
    [storage],
  );

  const trySync = useCallback(async () => {
    const inst = await storage.getInstallation();
    if (!inst || inst.shareToken === "demo" || !navigator.onLine) return;
    try {
      const days = await storage.getDays();
      const local = aggregateFromDays(days);
      const remote = await syncProgress(inst.credential, local);
      await storage.setLastSyncProgress(remote);
      setSyncStatus("Gesynchroniseerd");
    } catch {
      setSyncStatus("Sync later opnieuw");
    }
  }, [storage]);

  useEffect(() => {
    void (async () => {
      const inst = await storage.getInstallation();
      if (inst) {
        setDemoMode(inst.shareToken === "demo");
        setAgeGroup(inst.ageGroup);
        setPreferredTime(inst.preferredTime);
        await refreshToday(inst.seed, inst.ageGroup);
        setPhase("main");
        void trySync();
        return;
      }
      if (!allowActivation || !shareToken || !ageGroupFromLink) {
        if (demoMode && ageGroup) {
          setPhase("onboarding");
          return;
        }
        setPhase("landing");
        return;
      }
      setAgeGroup(ageGroupFromLink);
      setPhase("onboarding");
    })();
  }, [storage, shareToken, ageGroupFromLink, allowActivation, demoMode, ageGroup, refreshToday, trySync]);

  useEffect(() => {
    const onOnline = () => void trySync();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [trySync]);

  const updateStatus = async (status: DayStatus, score?: 1 | 2 | 3) => {
    if (!today) return;
    const next: LocalDayRecord = {
      ...today,
      status,
      ...(score ? { discomfortScore: score } : {}),
    };
    await storage.upsertDay(next);
    setToday(next);
    void trySync();
  };

  const onDone = () => setPhase("score");

  const onScore = async (score: 1 | 2 | 3) => {
    await updateStatus("done", score);
    if (notifications) await notifications.cancelSnooze();
    setPhase("main");
  };

  const onSkip = async () => {
    await updateStatus("skipped");
    if (notifications) await notifications.cancelSnooze();
  };

  const onSnooze = async () => {
    await updateStatus("snoozed");
    if (notifications) await notifications.scheduleSnooze();
  };

  const onFinishTime = () => {
    if (demoMode) {
      void onActivateDemo();
      return;
    }
    setPhase("activate");
  };

  const onActivateDemo = async () => {
    if (!ageGroup) return;
    const seed = `demo-${crypto.randomUUID()}`;
    const inst = {
      shareToken: "demo",
      installationId: "demo",
      credential: "demo",
      seed,
      ageGroup,
      preferredTime,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      activatedAt: new Date().toISOString(),
    };
    await storage.setInstallation(inst);
    if (notifications) {
      const ok = await notifications.requestPermission();
      if (ok) await notifications.scheduleDaily(preferredTime);
    }
    await refreshToday(seed, ageGroup);
    setPhase("main");
  };

  const onLandingTap = () => {
    if (!enableDemoUnlock) return;
    const next = landingTaps + 1;
    setLandingTaps(next);
    if (next >= 5) {
      setDemoMode(true);
      setAgeGroup("12-15");
      setPhase("onboarding");
    }
  };

  const onActivate = async () => {
    if (!shareToken || !ageGroup) return;
    setError(null);
    try {
      const deviceInstallationId = crypto.randomUUID();
      const res = await activateInstallation(shareToken, deviceInstallationId);
      const inst = {
        shareToken,
        installationId: res.installationId,
        credential: res.credential,
        seed: res.seed,
        ageGroup: res.ageGroup,
        preferredTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        activatedAt: new Date().toISOString(),
      };
      await storage.setInstallation(inst);
      if (notifications) {
        const ok = await notifications.requestPermission();
        if (ok) await notifications.scheduleDaily(preferredTime);
      }
      await refreshToday(res.seed, res.ageGroup);
      setPhase("main");
      void trySync();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Activatie mislukt");
      setPhase("expired");
    }
  };

  const ageLabel = ageGroup ? AGE_LABELS[ageGroup] : "";

  const statusLabel =
    today?.status === "done"
      ? "Gedaan vandaag"
      : today?.status === "skipped"
        ? "Overgeslagen vandaag"
        : today?.status === "snoozed"
          ? "Gesnoozed — later vandaag"
          : null;

  if (phase === "loading") {
    return <div className="dig-client dig-client--center">Laden…</div>;
  }

  if (phase === "landing") {
    return (
      <div
        className="dig-client dig-client--center dig-client--landing"
        onClick={onLandingTap}
        role="presentation"
      >
        <h1>Doe iets geks</h1>
        <p>Open de app via de link van je behandelaar om te starten.</p>
        <p className="dig-client__muted">
          Heb je nog geen link? Vraag je behandelaar om een deellink.
        </p>
      </div>
    );
  }

  if (phase === "onboarding") {
    return (
      <div className="dig-client">
        {demoMode ? <p className="dig-client__demo-badge">Demo</p> : null}
        <h1>Doe iets geks</h1>
        <p>
          Elke dag krijg je een opdracht om je gedachten uit te dagen — op een veilige,
          speelse manier.
        </p>
        {demoMode ? (
          <label className="dig-client__time-label">
            Leeftijd
            <select
              value={ageGroup ?? "12-15"}
              onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
            >
              {AGE_GROUPS.map((age) => (
                <option key={age} value={age}>
                  {AGE_LABELS[age]}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p>
            <strong>Leeftijd:</strong> {ageLabel}
          </p>
        )}
        <ul className="dig-client__safety">
          <li>Doe alleen wat veilig voelt.</li>
          <li>Stop als iets echt eng is.</li>
          <li>Vraag hulp als je twijfelt.</li>
        </ul>
        <button type="button" className="jari-btn jari-btn--primary" onClick={() => setPhase("time")}>
          Verder
        </button>
      </div>
    );
  }

  if (phase === "time") {
    return (
      <div className="dig-client">
        <h2>Wanneer wil je je herinnering?</h2>
        <p className="dig-client__muted">Je krijgt een korte melding. De opdracht zie je in de app.</p>
        <label className="dig-client__time-label">
          Tijdstip
          <input
            type="time"
            value={preferredTime}
            onChange={(e) => setPreferredTime(e.target.value)}
          />
        </label>
        <button type="button" className="jari-btn jari-btn--primary" onClick={onFinishTime}>
          Klaar
        </button>
      </div>
    );
  }

  if (phase === "activate") {
    return (
      <div className="dig-client">
        <h2>Activeren</h2>
        <p>We koppelen je link aan deze app. Daarna werkt alles offline.</p>
        {error ? <p className="dig-client__error">{error}</p> : null}
        <button type="button" className="jari-btn jari-btn--primary" onClick={() => void onActivate()}>
          Activeer
        </button>
      </div>
    );
  }

  if (phase === "expired") {
    return (
      <div className="dig-client dig-client--center">
        <h2>Link niet geldig</h2>
        <p>{error ?? "Deze link is verlopen of ingetrokken."}</p>
        <p className="dig-client__muted">Vraag je behandelaar om een nieuwe link.</p>
      </div>
    );
  }

  if (phase === "score") {
    return (
      <div className="dig-client">
        <h2>Hoe erg vond je het om dit te doen?</h2>
        <div className="dig-client__scores">
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              type="button"
              className="jari-btn dig-client__score-btn"
              onClick={() => void onScore(n as 1 | 2 | 3)}
            >
              {n}
            </button>
          ))}
        </div>
        <p className="dig-client__muted">1 = niet erg · 3 = best erg</p>
      </div>
    );
  }

  return (
    <div className="dig-client">
      {demoMode ? <p className="dig-client__demo-badge">Demo</p> : null}
      <p className="dig-client__date">{localDateKey()}</p>
      <p className="dig-client__prompt">{promptText}</p>
      {statusLabel ? <p className="dig-client__status">{statusLabel}</p> : null}
      {today?.status === "pending" || today?.status === "snoozed" ? (
        <div className="dig-client__actions">
          <button type="button" className="jari-btn" onClick={() => void onSnooze()}>
            Snooze
          </button>
          <button type="button" className="jari-btn jari-btn--primary" onClick={onDone}>
            Gedaan
          </button>
          <button type="button" className="jari-btn" onClick={() => void onSkip()}>
            Ga ik niet doen
          </button>
        </div>
      ) : null}
      {syncStatus ? <p className="dig-client__sync">{syncStatus}</p> : null}
    </div>
  );
}
